import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { faLock } from '@fortawesome/pro-solid-svg-icons/faLock';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  namespaces,
  useSharedTranslation,
} from 'us-react-components/dist/hooks/useSharedTranslation';

import { axios } from 'us-web-services';
import { Modal } from 'us-react-components/dist/Modal/Modal';

import { Button, ButtonType } from '../../../components';

const NOTE_MAX = 300;
const SITTER_NOTES_URL = '/v2/sitter-notes';

export interface ParentPrivateNote {
  id?: number;
  comment?: string;
  updated?: string;
}

interface Props {
  isOpen: boolean;
  onClose: () => void;
  sitterId: number;
  parentId: number;
  initialNote?: ParentPrivateNote;
  onSaved?: (note?: ParentPrivateNote) => void;
}

export const ParentNoteModal = ({
  isOpen,
  onClose,
  sitterId,
  parentId,
  initialNote,
  onSaved,
}: Props): JSX.Element => {
  const { t } = useSharedTranslation(namespaces.caregiver);
  const [comment, setComment] = useState(initialNote?.comment ?? '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (isOpen) {
      setComment(initialNote?.comment ?? '');
      setError('');
    }
  }, [isOpen, initialNote]);

  const save = async (): Promise<void> => {
    setSaving(true);
    setError('');
    try {
      const existingId = initialNote?.id;
      const now = new Date().toISOString();
      if (existingId && !comment.trim()) {
        await axios.delete(`${SITTER_NOTES_URL}/${existingId}`);
        onSaved?.(undefined);
      } else if (existingId) {
        const resp = await axios.patch(`${SITTER_NOTES_URL}/${existingId}`, {
          id: existingId,
          comment,
        });
        const saved = (resp.data?.data ?? { id: existingId, comment }) as ParentPrivateNote;
        onSaved?.({ ...saved, updated: saved.updated ?? now });
      } else if (comment.trim()) {
        const resp = await axios.post(SITTER_NOTES_URL, {
          sitterId,
          parentId,
          comment,
        });
        const saved = (resp.data?.data ?? { comment }) as ParentPrivateNote;
        onSaved?.({ ...saved, updated: saved.updated ?? now });
      }
      onClose();
    } catch {
      setError(t('pages.messageThread.noteSaveError'));
      toast.error(t('pages.messageThread.noteSaveError'));
    } finally {
      setSaving(false);
    }
  };

  const remaining = NOTE_MAX - comment.length;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={t('pages.messageThread.parentNoteModal.title')}
      className='w-full max-w-xl'
      footer={
        <>
          <Button buttonType={ButtonType.tertiary} onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button
            buttonType={ButtonType.primary}
            disabled={saving}
            onClick={save}
          >
            {t('common.save')}
          </Button>
        </>
      }
    >
      <div className='text-right italic text-sm mb-1'>
        {t('pages.messageThread.parentNoteModal.charactersRemaining', { count: remaining })}
      </div>
      <textarea
        aria-label={t('pages.messageThread.parentNoteModal.noteAriaLabel')}
        className='form-control w-full border border-neutral-300 rounded p-2 mb-2'
        rows={6}
        maxLength={NOTE_MAX}
        value={comment}
        onChange={(e): void => setComment(e.target.value)}
      />
      <p className='text-sm'>
        <FontAwesomeIcon icon={faLock} className='mr-1' aria-hidden='true' />
        {t('pages.messageThread.parentNoteModal.privacyHint')}
      </p>
      {error && <p className='text-red-600 text-sm mt-2'>{error}</p>}
    </Modal>
  );
};
